import { Locale } from '@headless-workspace/config';
import {
    Repository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import { initRequestStrategy } from '@headless-workspace/data';
import { LanguageHelpers } from '@headless-workspace/utils';
import { LocalitiesAutocompleteDTO } from './LocalitiesAutocompleteDTO';
import { LocalityDetails, mapLoyaltyDetails } from './LocalityDetails';
import { LocalityDetailsDTO } from './LocalityDetailsDTO';
import { LocalityValue, mapLocalities } from './LocalityValue';
import { SearchGeolocationsAddressDTO } from './SearchGeolocationAddressDTO';
import { mapSearchGeolocationAddress, SearchGeolocationAddressValue } from './SearchGeolocationAddressValue';

export interface SearchGeolocationRepositorySpec {
    fetchAddressFromGeolocation(
        latitude: number,
        longitude: number,
        key: string,
    ): Promise<Result<SearchGeolocationAddressValue>>;

    fetchAddressAutoComplete(
        input: string,
        key: string,
        custom_description: string,
        locale: Locale,
    ): Promise<Result<LocalityValue[]>>;

    fetchLocalityDetails(public_id: string, key: string): Promise<Result<LocalityDetails>>;
}

export class SearchGeolocationRepository extends Repository implements SearchGeolocationRepositorySpec {
    path = '/localities';

    constructor(apiBaseUrl: string, requestStrategy = initRequestStrategy()) {
        super(
            requestStrategy.authStrategy,
            requestStrategy.refreshTokenStrategy,
            requestStrategy.queryParamStrategy,
            apiBaseUrl,
        );
    }

    async fetchAddressAutoComplete(
        input: string,
        key: string,
        custom_description: string,
        locale: Locale,
    ): Promise<Result<LocalityValue[]>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/autocomplete`, {
                key,
                input,
                custom_description,
                components: `country:${LanguageHelpers.parseCountry(locale).toLocaleLowerCase()}`,
                language: LanguageHelpers.parseLanguage(locale),
            });
            const localities = LocalitiesAutocompleteDTO.parse(response);

            if (localities.localities.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(mapLocalities(localities));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError);
        }
    }

    async fetchAddressFromGeolocation(
        latitude: number,
        longitude: number,
        key: string,
    ): Promise<Result<SearchGeolocationAddressValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/geocode`, {
                key,
                latlng: `${latitude},${longitude}`,
            });
            const addressFromGeolocationResponse =
                SearchGeolocationsAddressDTO.parse(response).results.map(mapSearchGeolocationAddress);

            if (addressFromGeolocationResponse.length === 0) {
                return ResultEmpty();
            }

            // Woosmap car return multiple address for one latitude and longitude. We use the first one
            return ResultSuccess(addressFromGeolocationResponse[0]);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError);
        }
    }

    async fetchLocalityDetails(public_id: string, key: string): Promise<Result<LocalityDetails>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/details`, {
                key,
                public_id,
            });

            const localityDetailsDTO = LocalityDetailsDTO.parse(response);

            return ResultSuccess(mapLoyaltyDetails(localityDetailsDTO));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError);
        }
    }
}
